/**
 * travelRates — versionierte Sätze-Konfiguration für die Dienstreisen-/Reisekostenerfassung (RK).
 *
 * Source of Truth (SoT) für Verpflegungspauschalen, Mahlzeitenkürzung und Kilometergeld.
 * Reine Daten-Deklaration, KEINE Firebase-/React-Imports — voll unit-testbar.
 *
 * Pflege-Prozess (architecture-reisekosten.md §4.2): ein BMF-Update ist ein Daten-Commit,
 * kein Code-Delta — neuen Eintrag mit passendem `validFromISO` anhängen, den Vorgänger-Eintrag
 * mit `validToISO` schließen und `versionId` hochziehen. NIE einen bestehenden Eintrag
 * rückwirkend überschreiben (GoBD-Nachvollziehbarkeit: der Settlement-Snapshot referenziert
 * `versionId`, alte Reisen müssen mit den zum Zeitpunkt gültigen Sätzen reproduzierbar bleiben).
 *
 * @module travelRates
 */

/**
 * Ein Verpflegungspauschale-Satz für ein Land, gültig in einem Zeitfenster.
 * MVP: ausschließlich 'DE'-Einträge. Seit RK-11 (Ausbau-Welle A, architecture-reisekosten.md §13)
 * dient dieser Typ zusätzlich als Eintragsform für die separate Auslands-Konfiguration
 * (`TravelForeignRatesConfig.entries` weiter unten) — `regionKey`/`regionLabel` sind dafür
 * die einzigen neuen (optionalen) Felder; DE-Einträge tragen sie nie.
 */
export interface TravelPerDiemRateEntry {
  /** ISO-3166-1 alpha-2 Ländercode. MVP-Inlandskonfiguration: nur 'DE'. */
  countryCode: string;
  /** Gültig ab (inklusiv), 'YYYY-MM-DD'. */
  validFromISO: string;
  /** Gültig bis (inklusiv), 'YYYY-MM-DD'; `null` = zeitlich offen (aktuell gültig). */
  validToISO: string | null;
  /** Voller Tagessatz (Zwischentag mehrtägiger Reise) in Cent. DE ab 2020: 2800 (28,00 €). */
  fullDayCents: number;
  /** An-/Abreisetag bzw. eintägig >8h in Cent. DE: 1400 (14,00 €). */
  arrivalDepartureDayCents: number;
  /**
   * Übernachtungspauschale bei ARBEITGEBER-Erstattung in Cent, oder `null` wenn nicht anwendbar.
   * DE: 2000 (20,00 €) — gilt NUR für Arbeitnehmer-Erstattung (Folgewelle RK-W2-5) und wird
   * im MVP (Unternehmer-Selbstabrechnung) NIEMALS gebucht (architecture-reisekosten.md §3.1).
   * Für Auslands-Einträge (RK-11ff.): existiert ebenfalls nur als Datenfeld, wird NIE gebucht
   * (V4/V20, architecture-reisekosten.md §9/§13.11) — nur AG-Erstattung, außerhalb des MVP.
   */
  overnightEmployerFlatCents: number | null;
  /** Rechtsgrundlage bzw. BMF-Aktenzeichen. Bei Auslands-Einträgen: Verweis auf die Vintage-Metadaten. */
  sourceNote: string;
  /**
   * NEU (RK-11, §13.2): stabiler Städte-/Regions-Slug (z. B. `'new-york'`).
   * `undefined`/`null` = Land-Ebene bzw. „im Übrigen" (BMF-Sammelzeile für ein Land ohne
   * eigene Stadt-Zeile). Inlands-(DE-)Einträge tragen dieses Feld NIE — es existiert
   * ausschließlich für die separate Auslands-Konfiguration (`TravelForeignRatesConfig`).
   */
  regionKey?: string | null;
  /**
   * NEU (RK-11, §13.2): Anzeige-Label aus dem BMF-Schreiben (z. B. `'New York'`), deutsch,
   * BMF-Originalbezeichnung. Nur gesetzt, wenn `regionKey` gesetzt ist.
   */
  regionLabel?: string | null;
}

/** Mahlzeitenkürzung als Prozentsatz vom vollen Tagessatz, gültig in einem Zeitfenster. */
export interface TravelMealReductionEntry {
  /** Gültig ab (inklusiv), 'YYYY-MM-DD'. */
  validFromISO: string;
  /** Gültig bis (inklusiv), 'YYYY-MM-DD'; `null` = zeitlich offen. */
  validToISO: string | null;
  /** Kürzung Frühstück in Prozent vom vollen Tagessatz. Seit 2020: 20. */
  breakfastPct: number;
  /** Kürzung Mittagessen in Prozent vom vollen Tagessatz. Seit 2020: 40. */
  lunchPct: number;
  /** Kürzung Abendessen in Prozent vom vollen Tagessatz. Seit 2020: 40. */
  dinnerPct: number;
}

/** Kilometergeld-Satz je gefahrenem Kilometer, gültig in einem Zeitfenster. */
export interface TravelMileageRateEntry {
  /** Gültig ab (inklusiv), 'YYYY-MM-DD'. */
  validFromISO: string;
  /** Gültig bis (inklusiv), 'YYYY-MM-DD'; `null` = zeitlich offen. */
  validToISO: string | null;
  /** Kilometergeld PKW in Cent pro km. 30 (0,30 €/km). */
  carCentsPerKm: number;
  /** Kilometergeld Motorrad in Cent pro km. 20 (0,20 €/km). */
  motorcycleCentsPerKm: number;
}

/** Vollständige, versionierte Sätze-Konfiguration (SoT). */
export interface TravelRatesConfig {
  /** Version-Kennung, wird in den Settlement-Snapshot geschrieben (Nachvollziehbarkeit). */
  versionId: string;
  /** Alle bekannten Verpflegungspauschale-Einträge (mehrere Länder/Zeiträume möglich). */
  perDiem: readonly TravelPerDiemRateEntry[];
  /** Alle bekannten Mahlzeitenkürzungs-Einträge. */
  mealReductions: readonly TravelMealReductionEntry[];
  /** Alle bekannten Kilometergeld-Einträge. */
  mileage: readonly TravelMileageRateEntry[];
}

/**
 * MVP-Sätze-Konfiguration (Inland, Unternehmer-Selbstabrechnung).
 *
 * WICHTIG (load-bearing): `fullDayCents` ist 2800 (28,00 €), NIEMALS 3200 — die im
 * Wachstumschancengesetz ursprünglich vorgesehene Erhöhung der Verpflegungspauschalen
 * wurde im Gesetzgebungsverfahren GESTRICHEN. Die Sätze 28/14 gelten unverändert seit
 * 2020 bis inkl. 2026 (architecture-reisekosten.md §4.2, Recht-Recherche §1.2).
 *
 * Historische Vor-2020-Sätze (24 €/12 €) werden bewusst NICHT aufgenommen — Reisen vor
 * dem 01.01.2020 sind kein MVP-Use-Case; der Resolver liefert für sie `null` (fail-closed),
 * statt einen falschen Satz zu raten.
 */
export const TRAVEL_RATES: TravelRatesConfig = {
  versionId: 'travel-rates-de-2020-v1',
  perDiem: [
    {
      countryCode: 'DE',
      validFromISO: '2020-01-01',
      validToISO: null,
      fullDayCents: 2800,
      arrivalDepartureDayCents: 1400,
      overnightEmployerFlatCents: 2000,
      sourceNote: '§9 Abs.4a S.3 EStG',
    },
  ],
  mealReductions: [
    {
      validFromISO: '2020-01-01',
      validToISO: null,
      breakfastPct: 20,
      lunchPct: 40,
      dinnerPct: 40,
    },
  ],
  mileage: [
    {
      validFromISO: '2020-01-01',
      validToISO: null,
      carCentsPerKm: 30,
      motorcycleCentsPerKm: 20,
    },
  ],
};

/**
 * Ein Jahrgang der Auslands-Sätze-Konfiguration mit Herkunfts-/GoBD-Metadaten.
 *
 * NEU (RK-11, architecture-reisekosten.md §13.2). Jeder Jahrgang referenziert genau EIN
 * BMF-Schreiben (Az. + BStBl-Fundstelle) sowie den SHA-256-Hash der eingecheckten Quell-PDF
 * (Herkunftsnachweis, GoBD-Sorgfaltspflicht — §13.3 Update-Pipeline, RK-12/RK-13).
 */
export interface TravelForeignRatesVintage {
  /** Gültig ab (inklusiv), z. B. `'2026-01-01'`. */
  validFromISO: string;
  /** Gültig bis (inklusiv); `null` = zeitlich offen (aktuell gültiger Jahrgang). */
  validToISO: string | null;
  /** BMF-Aktenzeichen, z. B. `'IV C 5 - S 2353/00094/007/012'`. */
  sourceAz: string;
  /** BStBl-Fundstelle, z. B. `'BStBl 2025 I S. 2078'`. */
  sourceBstbl: string;
  /** SHA-256-Hash der eingecheckten Original-Quell-PDF (Herkunftsnachweis). */
  sourcePdfSha256: string;
}

/**
 * Vollständige, versionierte Auslands-Sätze-Konfiguration (§13.2).
 *
 * NEU (RK-11). Bewusst GETRENNT von {@link TravelRatesConfig} (E9,
 * architecture-reisekosten.md §13.1): generierte Auslandsdaten dürfen die handgepflegte
 * Inlands-SoT nie berühren. Der Rechenkern (`calculateTravelAllowances`) erhält diese
 * Konfiguration ausschließlich als additiv-optionalen Parameter (`TravelCalcOptions.foreignRates`,
 * RK-14) — NIE über einen impliziten Modul-Import. Der reale Dateninhalt
 * (`TRAVEL_RATES_FOREIGN`, generiertes Asset `travelRatesForeign.generated.ts`) ist NICHT Teil
 * von RK-11, sondern wird von RK-13 aus dem BMF-PDF erzeugt (halbautomatische Pipeline, §13.3)
 * und per menschlichem Freigabe-Gate (O-12) gemergt.
 */
export interface TravelForeignRatesConfig {
  /** Version-Kennung, wandert in den Settlement-Snapshot (`foreignRatesVersionId`, §13.9). */
  versionId: string;
  /**
   * NEU (A-6): Zusicherungs-Horizont 'YYYY-MM-DD' (inklusiv). Reisetage NACH diesem Datum
   * werden im Auslands-Zweig fail-closed verweigert (`foreignRatesNotYetAvailableForDate`).
   * Gesetzt AUSSCHLIESSLICH vom Generator (Manifest-`assurance`-Block). PFLICHT — kein
   * Default, kein Fallback.
   *
   * Interner Freigabestand des Datenbestands. KEINE Zusicherung oder Garantie
   * gegenüber Nutzern (§ 443 BGB); wird kundenseitig nie ausgegeben.
   *
   * Rechtlicher Kontext: Die Pauschbeträge setzt das BMF nach § 9 Abs. 4a S. 5 EStG
   * fest (für Selbständige/Unternehmer über § 4 Abs. 5 S. 1 Nr. 5 EStG i. V. m. § 9 Abs. 4a
   * EStG) und schreibt sie in der Regel jährlich fort — die Norm ordnet KEINE jährliche
   * Neufestsetzung an; ohne Nachfolgeschreiben gelten die Beträge fort (Fortgeltungs-Pfad,
   * scripts/travel-rates/README.md). Dieses Feld sagt daher NICHT „danach ungültig", sondern
   * „Fortbestand nur bis hierher geprüft".
   *
   * Spec: architecture-travel-rates-validity.md §4.1 / D-1 / A-8.
   */
  assuredUntilISO: string;
  /** Alle bekannten Jahrgänge (Metadaten je BMF-Schreiben). */
  vintages: readonly TravelForeignRatesVintage[];
  /**
   * Alle bekannten Auslands-Einträge (je Land, ggf. + Region, × Jahrgang).
   * PFLICHT: enthält je Jahrgang mindestens einen `'LU'`-Eintrag (Luxemburg-Auffangsatz,
   * E11/V18 — Skript-Plausibilitäts-Pflicht, §13.3 Schritt 4). Enthält NIE einen `'DE'`-Eintrag
   * (V16 — Indikator für eine falsche/verwechselte Tabelle, siehe Recherche §2.2).
   */
  entries: readonly TravelPerDiemRateEntry[];
}
